import { Node, mergeAttributes } from '@tiptap/core';
/* eslint-disable-next-line @typescript-eslint/no-explicit-any */
type AnyMd = any;

/**
 * Atom node that preserves inline LaTeX math ($...$) verbatim through
 * the editor. tiptap-markdown's text serializer escapes backslashes,
 * braces, and other chars inside prose — this node sidesteps that by
 * never running the text through the serializer escape logic.
 *
 * Parse: markdown-it inline rule → <span data-math="..."> → InlineMath node
 * Serialize: write `$content$` directly (no escaping)
 */
export const InlineMath = Node.create({
  name: 'inlineMath',
  group: 'inline',
  inline: true,
  atom: true,
  selectable: true,

  addAttributes() {
    return {
      content: {
        default: '',
        parseHTML: (el) => (el as HTMLElement).getAttribute('data-math') ?? '',
        renderHTML: (attrs) => ({ 'data-math': attrs.content as string }),
      },
    };
  },

  parseHTML() {
    return [{ tag: 'span[data-math]' }];
  },

  renderHTML({ node, HTMLAttributes }) {
    const tex = node.attrs.content as string;
    return ['span', mergeAttributes(HTMLAttributes, { class: 'math-inline' }), `$${tex}$`];
  },

  addStorage() {
    return {
      markdown: {
        /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
        serialize(state: any, node: any) {
          /* Write verbatim — never call state.text() which would escape backslashes */
          state.write(`$${node.attrs.content as string}$`);
        },
        parse: {
          setup(md: AnyMd) {
            /* Inline rule: $...$ → math_inline token */
            /* Mirrors the same heuristic used by mathToPlainText in latexText.ts: */
            /* non-space right after opening $ and right before closing $. */
            md.inline.ruler.before('backticks', 'math_inline', (state: AnyMd, silent: boolean) => {
              if (state.src[state.pos] !== '$') return false;
              /* Let BlockMath handle $$...$$; don't corrupt it into empty inline tokens. */
              if (state.src[state.pos + 1] === '$') return false;

              const start = state.pos + 1;
              if (start >= state.posMax) return false;

              /* Opening $ must not be followed by whitespace */
              const startChar = state.src[start];
              if (startChar === ' ' || startChar === '\t' || startChar === '\n') return false;

              let pos = start;
              while (pos < state.posMax) {
                const ch = state.src[pos];
                if (ch === '$') {
                  /* Closing $ must not be preceded by whitespace */
                  if (state.src[pos - 1] === ' ' || state.src[pos - 1] === '\t') {
                    pos++;
                    continue;
                  }
                  if (!silent) {
                    const content = state.src.slice(start, pos);
                    const token = state.push('math_inline', '', 0);
                    token.content = content;
                  }
                  state.pos = pos + 1;
                  return true;
                }
                if (ch === '\n') break;
                pos++;
              }
              return false;
            });

            /* Renderer: output a span with data-math attribute */
            md.renderer.rules['math_inline'] = (tokens: AnyMd[], idx: number) => {
              const content = tokens[idx].content as string;
              const escaped = content.replace(/&/g, '&amp;').replace(/"/g, '&quot;');
              return `<span data-math="${escaped}">$${content}$</span>`;
            };
          },
        },
      },
    };
  },
});
